import React, { useState } from "react";
import { useRacketLabAdminT } from "../../i18n/racketLabAdminI18n";

/**
 * ConfirmActionDialog — generic confirmation modal for medium-sensitivity
 * mutations. Used by Sub-sprint B for: toggle status · ticket transitions ·
 * shift open/close/cancel.
 *
 * Renders a button (children) that opens an inline modal. On confirm, calls
 * `onConfirm()`. Pure controlled component — caller is responsible for
 * the actual mutation.
 *
 * QUICK 1.B i18n migration: the default "Cancel" and "Confirm" labels are
 * resolved through `racketLab.common.actions.*`. Callers that pass an
 * explicit `confirmLabel` keep their current behavior (existing literal
 * strings render as before until those callers are migrated in later
 * sub-sprints). `dataTestId`s are unchanged.
 */
export const ConfirmActionDialog = ({
  trigger,
  title,
  description,
  confirmLabel,
  onConfirm,
  disabled = false,
  tone = "default",
  dataTestId = "rl-confirm-action",
}) => {
  const t = useRacketLabAdminT();
  const [open, setOpen] = useState(false);
  const handleConfirm = async () => {
    try { await onConfirm(); setOpen(false); }
    catch { /* error toast handled by caller */ }
  };

  const triggerWithClick = React.cloneElement(trigger, {
    onClick: (e) => { e.preventDefault(); if (!disabled) setOpen(true); },
    "data-testid": `${dataTestId}-trigger`,
  });

  if (!open) return triggerWithClick;

  const cls = tone === "danger"
    ? "bg-rose-600 hover:bg-rose-700"
    : "bg-emerald-600 hover:bg-emerald-700";

  // Resolve default labels lazily so callers can still inject explicit
  // strings from their own copy. Empty string ("") also falls back to
  // the i18n default so callers don't accidentally render a blank button.
  const finalConfirmLabel = (confirmLabel != null && confirmLabel !== "")
    ? confirmLabel
    : t("common.actions.confirm");

  return (
    <>
      {triggerWithClick}
      <div
        data-testid={`${dataTestId}-modal`}
        className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40"
        onClick={() => setOpen(false)}
      >
        <div
          className="w-full max-w-md rounded-md bg-white p-5 shadow-lg"
          onClick={(e) => e.stopPropagation()}
        >
          <h3 className="text-base font-semibold text-slate-900">{title}</h3>
          {description && (
            <p className="mt-2 text-sm text-slate-600">{description}</p>
          )}
          <div className="mt-4 flex justify-end gap-2">
            <button
              type="button"
              data-testid={`${dataTestId}-cancel`}
              className="rounded-md border border-slate-300 px-3 py-1.5 text-xs hover:bg-slate-50"
              onClick={() => setOpen(false)}
            >{t("common.actions.cancel")}</button>
            <button
              type="button"
              data-testid={`${dataTestId}-confirm`}
              className={`rounded-md px-3 py-1.5 text-xs font-medium text-white ${cls}`}
              onClick={handleConfirm}
            >{finalConfirmLabel}</button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ConfirmActionDialog;
